import { useMemo } from 'react';

export type PhoneFormat = 'national' | 'international' | 'auto';

export const usePhoneFormatter = (value?: string, format: PhoneFormat = 'auto') => {
    /**
     * Formate une chaîne de caractères en numéro de téléphone
     * Supporte les formats :
     * - International : +XX X XX XX XX XX
     * - National : XX XX XX XX XX
     */
    const formattedValue = useMemo(() => {
        if (!value) return '';

        const hasCountryCode = value.startsWith('+');
        const cleaned = value.replace(/[^\d+]/g, '');

        const shouldUseInternational =
            format === 'international' || (format === 'auto' && hasCountryCode);

        if (shouldUseInternational) {
            const digits = cleaned.substring(1);

            if (digits.length <= 2) {
                return `+${digits}`;
            }

            const countryCode = digits.substring(0, 2);
            const phoneNumber = digits.substring(2, 12);

            let formatted = `+${countryCode}`;

            if (phoneNumber.length > 0) {
                formatted += ` ${phoneNumber.charAt(0)}`;

                for (let i = 1; i < phoneNumber.length; i += 2) {
                    const pair = phoneNumber.substring(i, i + 2);
                    if (pair.length > 0) {
                        formatted += ` ${pair}`;
                    }
                }
            }

            return formatted;
        }

        const digits = cleaned.substring(0, 10);
        let formatted = '';

        for (let i = 0; i < digits.length; i += 2) {
            const pair = digits.substring(i, i + 2);
            if (i > 0) formatted += ' ';
            formatted += pair;
        }

        return formatted;
    }, [value, format]);

    /**
     * Valide si un numéro de téléphone est correct
     */
    const isValid = useMemo(() => {
        if (!value) return false;

        const cleaned = value.replace(/[^\d+]/g, '');
        const hasCountryCode = cleaned.startsWith('+');

        if (hasCountryCode) {
            const digits = cleaned.substring(1);
            return digits.length >= 10 && digits.length <= 14;
        }

        return cleaned.length === 10;
    }, [value]);

    /**
     * Détermine si c'est un format international
     */
    const isInternational = useMemo(() => value?.trimStart().startsWith('+') || false, [value]);

    /**
     * Calcule la longueur maximale selon le format
     */
    const maxLength = useMemo(
        () => (isInternational ? 17 : 14), // +XX X XX XX XX XX = 17, XX XX XX XX XX = 14
        [isInternational],
    );

    /**
     * Validation des touches pour les événements clavier
     */
    const isValidKey = (key: string, cursorPosition: number, currentValue: string): boolean => {
        const allowedKeys = [
            'Backspace',
            'Delete',
            'Tab',
            'Escape',
            'Enter',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
        ];

        if (allowedKeys.includes(key)) return true;
        if (/\d/.test(key)) return true;

        // Permettre + uniquement en première position
        if (key === '+') {
            return cursorPosition === 0 && currentValue.length === 0;
        }

        return false;
    };

    /**
     * Validation du texte collé
     */
    const isValidPaste = (
        pastedText: string,
        cursorPosition: number,
        currentValue: string,
    ): boolean => {
        // Vérifier que le texte collé ne contient que des chiffres, espaces, et éventuellement un + au début
        if (!/^[+]?[\d\s]*$/.test(pastedText)) return false;

        // Si le texte commence par +, s'assurer qu'on colle au début
        if (pastedText.startsWith('+')) {
            return cursorPosition === 0 && currentValue.length === 0;
        }

        return true;
    };

    return {
        formattedValue,
        isValid,
        isInternational,
        maxLength,
        isValidKey,
        isValidPaste,
    };
};
